//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { VALIDATION_STATUS } from '@apis/catalog-validation/catalogValidationModel';
import { GoogleCatalogIngestionModalAction } from '@catalog-ingestion/actions/googleCatalogIngestionModalActions';
import { GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS, WORKFLOW_PANELS } from '@catalog-ingestion/constants/googleCatalogIngestionModalConstants';
import { THRESHOLD_PERCENTAGE } from '@catalog-ingestion/constants/reviewPanelConstants';
import { IGoogleCatalogIngestionModal } from '@catalog-ingestion/models/GoogleCatalogIngestionModalModel';

const initialState: IGoogleCatalogIngestionModal = {
  isOpen: false,
  isThresholdPassed: false,
  isCatalogOnboarded: null,
  currentPanel: WORKFLOW_PANELS.CONNECT_PANEL,
  validationToken: '',
  validationStatus: VALIDATION_STATUS.PENDING,
  processedRows: 0,
  successfulRows: 0,
  failedRows: 0,
  validSample: null,
  invalidSample: null,
  validationResults: {
    results: [],
    summary: null
  },
  fullErrorData: null,
  expandingErrorData: null,
  flags: {
    isScheduling: false,
    isRetrievingStatus: false,
    isRetrievingValidation: false,
    isCatalogOnboarding: false,
    isRetrievingValidSample: false,
    isRetrievingInvalidSample: false,
    isRetrievingValidationResults: false,
  },
};

export default function GoogleCatalogIngestionModalReducer(
  state: IGoogleCatalogIngestionModal = initialState,
  action: GoogleCatalogIngestionModalAction,
): IGoogleCatalogIngestionModal {
  switch ( action.type ) {
    case GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.RESET_STATE:
      return {
        ...initialState,
      };
    case GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.OPEN_MODAL:
      return {
        ...state,
        isOpen: true,
      };
    case GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.CLOSE_MODAL:
      return {
        ...state,
        isOpen: false,
        currentPanel: WORKFLOW_PANELS.CONNECT_PANEL,
      };
    case GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.SET_FULL_ERROR_DATA:
      return {
        ...state,
        fullErrorData: action.fullErrorData,
      };
    case GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.SET_EXPANDING_ERROR_DATA:
      return {
        ...state,
        expandingErrorData: action.expandingErrorData,
      };
    case GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.CHANGE_PANEL:
      // this funtion will allow non-linear panel changes
      const panelVerificationCheck = true;
      switch (action.newPanel) {
        case WORKFLOW_PANELS.CONNECT_PANEL:
          if (panelVerificationCheck) {
            return {
              ...state,
              currentPanel: action.newPanel,
            };
          } else {
            return { ...state, };
          }
        case WORKFLOW_PANELS.REVIEW_PANEL:
          if (panelVerificationCheck) {
            return {
              ...state,
              currentPanel: action.newPanel,
            };
          } else {
            return { ...state, };
          }
        case WORKFLOW_PANELS.PROCESS_PANEL:
          return {
            ...state,
            currentPanel: action.newPanel,
          };
      }
    case GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.REQUEST_IS_SCHEDULING:
      return {
        ...state,
        flags: {
          ...state.flags,
          isScheduling: true,
        }
      };
    case GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.RECEIVE_IS_SCHEDULING:
      return {
        ...state,
        flags: {
          ...state.flags,
          isScheduling: false,
        },
        validationToken: action.validationToken
      };
    case GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.REQUEST_VALIDATION_STATUS:
      return {
        ...state,
        flags: {
          ...state.flags,
          isRetrievingStatus: true,
        }
      };
    case GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.RECEIVE_VALIDATION_STATUS:
      return {
        ...state,
        flags: {
          ...state.flags,
          isRetrievingStatus: false,
        },
        validationStatus: action.validationStatus,
      };
    case GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.REQUEST_VALIDATION:
      return {
        ...state,
        flags: {
          ...state.flags,
          isRetrievingValidation: true,
        }
      };
    case GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.RECEIVE_VALIDATION:
      const summary = action.validationData.feedValidation.summary;
      const newThresholdPassed = ((summary.failedRows / summary.processedRows) * 100 < THRESHOLD_PERCENTAGE);
      return {
        ...state,
        flags: {
          ...state.flags,
          isRetrievingValidation: false,
        },
        processedRows: action.validationData.feedValidation.summary.processedRows,
        successfulRows: action.validationData.feedValidation.summary.successfulRows,
        failedRows: action.validationData.feedValidation.summary.failedRows,
        isThresholdPassed: newThresholdPassed,
      };

    case GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.GET_VALID_SAMPLE:
      return {
        ...state,
        flags: {
          ...state.flags,
          isRetrievingValidSample: true,
        },
      };

    case GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.RECEIVE_VALID_SAMPLE:
      return {
        ...state,
        flags: {
          ...state.flags,
          isRetrievingValidSample: false,
        },
        validSample: action.validSample,
      };
    case GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.GET_INVALID_SAMPLE:
      return {
        ...state,
        flags: {
          ...state.flags,
          isRetrievingInvalidSample: true,
        },
      };

    case GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.RECEIVE_INVALID_SAMPLE:
      return {
        ...state,
        flags: {
          ...state.flags,
          isRetrievingInvalidSample: false,
        },
        invalidSample: action.invalidSample,
      };
    case GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.GET_VALIDATION_RESULTS:
      return {
        ...state,
        flags: {
          ...state.flags,
          isRetrievingValidationResults: true,
        },
      };

    case GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.RECEIVE_VALIDATION_RESULTS:
      const newValidationResults = {
        results: [...state.validationResults.results, ...action.validationResults.results],
        summary: action.validationResults.summary
      };
      return {
        ...state,
        flags: {
          ...state.flags,
          isRetrievingValidationResults: false,
        },
        validationResults: newValidationResults,
      };
    case GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.ONBOARD_CATALOG:
      return {
        ...state,
        flags: {
          ...state.flags,
          isCatalogOnboarding: true,
        },
      };

    case GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.CATALOG_ONBOARDED:
      return {
        ...state,
        isCatalogOnboarded: action.isOnboarded,
        flags: {
          ...state.flags,
          isCatalogOnboarding: false,
        },
      };
    default:
      return state;
  }
}
